import { useState } from 'react'
import { LoadingSpinner } from '../components/common/LoadingSpinner'

function ArrowPathIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  )
}
import { EmptyState } from '../components/common/EmptyState'
import { SortableHeader } from '../components/common/SortableHeader'
import { useSortableTable } from '../hooks/useSortableTable'
import { useEarningsCalendar, useRefreshEarningsCalendar } from '../hooks/useEarnings'
import type { EarningsCalendarEntry } from '../types/earnings'
import { getEarningsUrgencyColor, getEarningsUrgencyLabel } from '../types/earnings'

type UrgencyFilter = 'all' | 'soon' | 'upcoming' | 'later'

export function EarningsCalendarPage() {
  const { data, isLoading, error } = useEarningsCalendar(90)
  const refreshMutation = useRefreshEarningsCalendar()
  const [urgencyFilter, setUrgencyFilter] = useState<UrgencyFilter>('all')

  // Filter entries
  const filteredEntries = (data?.entries || []).filter((entry) => {
    if (urgencyFilter === 'all') return true
    if (urgencyFilter === 'soon') return entry.days_until <= 7
    if (urgencyFilter === 'upcoming') return entry.days_until > 7 && entry.days_until <= 30
    if (urgencyFilter === 'later') return entry.days_until > 30
    return true
  })

  const { sortedData, requestSort, getSortDirection } = useSortableTable<EarningsCalendarEntry>(
    filteredEntries,
    { key: 'days_until', direction: 'asc' }
  )

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  // Format currency
  const formatEstimate = (estimate?: number) => {
    if (estimate === undefined || estimate === null) return '-'
    return `$${estimate.toFixed(2)}`
  }

  const handleRefresh = () => {
    refreshMutation.mutate(undefined)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        Failed to load earnings calendar: {error.message}
      </div>
    )
  }

  const entries = data?.entries || []

  // Count by urgency
  const counts = {
    all: entries.length,
    soon: entries.filter((e) => e.days_until <= 7).length,
    upcoming: entries.filter((e) => e.days_until > 7 && e.days_until <= 30).length,
    later: entries.filter((e) => e.days_until > 30).length,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Earnings Calendar</h1>
          <p className="text-gray-500 mt-1">
            Upcoming earnings announcements for your holdings
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon
            className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => setUrgencyFilter('all')}
          className={`bg-white rounded-lg shadow-sm border p-4 text-left transition-colors ${
            urgencyFilter === 'all' ? 'border-primary-500 ring-1 ring-primary-500' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <p className="text-sm text-gray-500">All Upcoming</p>
          <p className="text-2xl font-bold text-gray-900">{counts.all}</p>
        </button>
        <button
          onClick={() => setUrgencyFilter('soon')}
          className={`bg-white rounded-lg shadow-sm border p-4 text-left transition-colors ${
            urgencyFilter === 'soon' ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <p className="text-sm text-gray-500">Within 7 Days</p>
          <p className="text-2xl font-bold text-red-600">{counts.soon}</p>
        </button>
        <button
          onClick={() => setUrgencyFilter('upcoming')}
          className={`bg-white rounded-lg shadow-sm border p-4 text-left transition-colors ${
            urgencyFilter === 'upcoming' ? 'border-yellow-500 ring-1 ring-yellow-500' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <p className="text-sm text-gray-500">7-30 Days</p>
          <p className="text-2xl font-bold text-yellow-600">{counts.upcoming}</p>
        </button>
        <button
          onClick={() => setUrgencyFilter('later')}
          className={`bg-white rounded-lg shadow-sm border p-4 text-left transition-colors ${
            urgencyFilter === 'later' ? 'border-gray-500 ring-1 ring-gray-500' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <p className="text-sm text-gray-500">30+ Days</p>
          <p className="text-2xl font-bold text-gray-600">{counts.later}</p>
        </button>
      </div>

      {/* Earnings Table */}
      {sortedData.length === 0 ? (
        <EmptyState
          icon="calendar"
          title="No Upcoming Earnings"
          description={
            entries.length === 0
              ? "None of your holdings have earnings announcements in the next 90 days."
              : "No earnings match your current filter."
          }
        />
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <SortableHeader
                    label="Symbol"
                    direction={getSortDirection('symbol')}
                    onSort={() => requestSort('symbol')}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left">
                  <SortableHeader
                    label="Report Date"
                    direction={getSortDirection('report_date')}
                    onSort={() => requestSort('report_date')}
                  />
                </th>
                <th className="px-6 py-3 text-left">
                  <SortableHeader
                    label="Days Until"
                    direction={getSortDirection('days_until')}
                    onSort={() => requestSort('days_until')}
                  />
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  EPS Estimate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedData.map((entry) => (
                <tr key={`${entry.symbol}-${entry.report_date}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-gray-900">{entry.symbol}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {entry.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(entry.report_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getEarningsUrgencyColor(
                        entry.days_until
                      )}`}
                    >
                      {getEarningsUrgencyLabel(entry.days_until)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatEstimate(entry.estimate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Refresh Status */}
      {refreshMutation.isSuccess && (
        <div className="text-sm text-green-600">
          Calendar refreshed: {refreshMutation.data.entries_updated} entries updated
        </div>
      )}
      {refreshMutation.isError && (
        <div className="text-sm text-red-600">
          Failed to refresh: {(refreshMutation.error as Error).message}
        </div>
      )}
    </div>
  )
}
